// modules
import { useCallback, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAppSelector, useAppDispatch } from "app/hooks/hooks";
import moment from "moment";

// components
import CountDownModal from "components/molecules/ModalContainer/CountDownModalContainer";

// constants
import { Routes, DateTimeFormats, APPROVE_RETRO_CLAIM as Constants } from "constants/global";

// redux
import { approveRetroClaim, retroCreateClaimDetailsSelector, retroClaimDetailsSelector, getLoadingStateSelector } from "redux/retroClaim/retroClaimSlice";
import { getConfigData, getConfigSelector } from "redux/userList/userSlice";

export type LocationState = {
	state: {
		retroClaimId: number;
		throughCreateClaim: boolean;
		outlet: string;
	};
};

const getSelector = (value: boolean) => {
	return value ? retroCreateClaimDetailsSelector : retroClaimDetailsSelector;
};

export function useApproveRetrospectiveClaim() {
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const dataLoading = useAppSelector(getLoadingStateSelector);

	const location = useLocation();
	const { state } = location as LocationState;

	const retroClaimDetails = useAppSelector(getSelector(state?.throughCreateClaim));

	const configDetails = useAppSelector(getConfigSelector);
	const { data: configDetailsData } = configDetails || {};

	const initValues = useMemo(() => {
		return {
			receipt_datetime: moment(retroClaimDetails?.receipt_datetime).format(DateTimeFormats.USE_AM_PM),
			outlet_code: state?.outlet,
			purchase_amount: retroClaimDetails?.purchase_amount,
			net_amount: retroClaimDetails?.net_amount
		};
	}, [retroClaimDetails, location, state]);

	const configDetailsVal = useMemo(() => {
		return configDetailsData?.[0]?.config_dtl_val3 ?? 365;
	}, [getConfigSelector]);

	const fieldErr = useMemo(() => {
		const dateDiff = moment(new Date(), DateTimeFormats.USE_SECONDS).diff(moment(initValues?.receipt_datetime, DateTimeFormats.USE_SECONDS), "days");
		if (dateDiff > configDetailsVal || dateDiff < 0) {
			return Constants.FIELD_RECEIPT_DATETIME;
		} else if (initValues?.net_amount && initValues?.net_amount < 10) {
			return Constants.FIELD_NETT_AMOUNT;
		}
		return "";
	}, [initValues, configDetailsVal]);

	const goToRetroClaim = useCallback(() => {
		navigate(`${Routes.RETRO_CLAIM}`);
	}, [navigate, dispatch]);

	const goBack = useCallback(() => {
		goToRetroClaim();
	}, [navigate, goToRetroClaim]);

	useEffect(() => {
		if (initValues?.receipt_datetime === DateTimeFormats.MOMENT_INVALID_DATE) {
			goBack();
		}
		dispatch(getConfigData({ config_dtl_code: "4004" }));
	}, [initValues]);

	const proceedToConfirm = useCallback(async () => {
		if (!fieldErr) {
			try {
				const { errors, message } = await dispatch(approveRetroClaim({ retro_claim_id: state?.retroClaimId })).unwrap();
				if (!errors?.length) {
					CountDownModal({
						timer: 3,
						title: message,
						onTimeComplete: goToRetroClaim
					});
				}
			} catch (error) {
				const err = error as any;
				if (err?.response?.data?.errors && err?.response?.data?.errors?.length > 0) {
					CountDownModal({
						timer: 3,
						errorIcon: true,
						title: err?.response?.data?.errors?.[0]?.message,
						closable: true
					});
				}
			}
		} else {
			if (fieldErr === Constants.FIELD_RECEIPT_DATETIME) {
				CountDownModal({
					timer: 3,
					title: Constants.RECEIPT_DATE_ERROR,
					onTimeComplete: goToRetroClaim,
					errorIcon: true
				});
			}
			if (fieldErr === Constants.FIELD_NETT_AMOUNT) {
				CountDownModal({
					timer: 3,
					title: Constants.NETT_AMOUNT_ERROR,
					onTimeComplete: goToRetroClaim,
					errorIcon: true
				});
			}
		}
	}, [dispatch, fieldErr, goToRetroClaim, approveRetroClaim]);

	return {
		initValues,
		loading: dataLoading === 1,
		goBack,
		proceedToConfirm
	};
}
